/**
 * #1023 — Polymarket US positions and trades for the whole org, in their own
 * panels under the Kalshi/Polymarket ones (the Trader Detail convention): US
 * holdings carry no average cost or P&L, so they stay out of the K/P cards
 * and totals rather than showing invented numbers there.
 *
 * Same authority as Trader Detail: the manager/admin role in the org, through
 * the org-scoped manager routes; each member's rows are what Trader Detail
 * reads for that member. Rendered only when the org has a US account.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { sportMatchesFilter, type SportFilter } from '@/lib/kalshiTicker/sportFilter';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import type { UsMarketClient } from '@/lib/polymarketUs/marketClient';
import { presentUsPosition, usPositionKey } from '@/lib/polymarketUs/portfolioModel';
import type { UsPositionRow } from '@/lib/polymarketUs/types';
import { ownerMatchesFilter, resolveOwnerAttribution, type OwnerAttribution, type OwnerFilterKey } from '@/lib/ownerAttribution';
import {
  checkUsAccounts, dash, readJson, traderPositions, UsFillMarket, UsFillPrice, UsFillTime, UsRetry, UsStatusRegion,
  US_MANAGER_API as API, type ManagerUsFill, type UsAccountCheck,
} from '@/views/TraderDetail/usManagerReads';
import { FilterNote } from '@/views/PolymarketUs/UsPortfolioPanel';
import { UsOrgSportLookup } from './usOrgSports';

/** `member: false` (#1055): the owner has left the org (deleted or moved), but
 *  their holdings are still on the org's accounts. The relay leaves them unnamed. */
type Member = { userId: string; username: string | null; member?: boolean };
type OrgPosition = { row: UsPositionRow; owner: OwnerAttribution; former: boolean; title: string | null };
type OrgFill = ManagerUsFill & { accountId: string; owners: OwnerAttribution[] };
type Loaded = {
  key: string;
  /** The page refresh this answer is for; a newer refreshKey means a re-read is in flight. */
  refresh: number;
  accounts: UsAccountCheck;
  positions: { rows: OrgPosition[]; complete: boolean } | null;
  fills: { rows: OrgFill[]; complete: boolean; unlistedFormerMembers: number } | null;
};

const FORMER_TITLE = "No longer in this org. These contracts are still on the org's Polymarket US account.";
const ownerOf = (m: Member) => resolveOwnerAttribution({ ownerKind: 'trader', ownerUserId: m.userId,
  ownerUsername: m.member === false ? `former member ${m.userId.slice(0, 8)}` : m.username });

/** Each member's accounts through Trader Detail's own mapping; one bad member refuses the read. */
function orgPositions(body: unknown): Loaded['positions'] {
  const b = body as { members?: unknown; complete?: unknown } | null;
  if (!Array.isArray(b?.members)) return null;
  const rows: OrgPosition[] = [];
  let complete = b.complete === true;
  for (const m of b.members as (Member & { accounts?: unknown; complete?: unknown })[]) {
    if (typeof m?.userId !== 'string') return null;
    const read = traderPositions(m, m.userId);
    if (!read) return null;
    if (!read.complete) complete = false;
    const owner = ownerOf(m);
    const former = m.member === false;
    rows.push(...read.rows.map((row) => ({ row, owner, former, title: read.titles.get(row.target.marketSlug) ?? null })));
  }
  return { rows, complete };
}

function orgFills(body: unknown): Loaded['fills'] {
  const b = body as { fills?: unknown; complete?: unknown; unlistedFormerMembers?: unknown } | null;
  if (!Array.isArray(b?.fills)) return null;
  const rows: OrgFill[] = [];
  for (const f of b.fills as (ManagerUsFill & { accountId?: unknown; owners?: unknown })[]) {
    if (typeof f?.executionId !== 'string' || typeof f.accountId !== 'string' || !Array.isArray(f.owners)
      || f.owners.length === 0 || !Array.isArray(f.disputed)) return null;
    rows.push({ ...f, accountId: f.accountId, owners: (f.owners as Member[]).map(ownerOf) });
  }
  const unlisted = b.unlistedFormerMembers;
  return { rows, complete: b.complete === true,
    unlistedFormerMembers: typeof unlisted === 'number' && Number.isSafeInteger(unlisted) && unlisted > 0 ? unlisted : 0 };
}

/** A fill claimed by two members' intents is disputed: shown, never pinned on one of them. */
function FillOwner({ owners }: { owners: OwnerAttribution[] }) {
  if (owners.length === 1) return <span className="text-foreground/90 text-xs" title={owners[0].label ?? undefined}>{owners[0].label}</span>;
  return (
    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400 light:text-amber-700"
      title={`Claimed by more than one trader: ${owners.map((o) => o.label).join(', ')}`}>
      disputed
    </span>
  );
}

/** `count` is null when the read failed: no number rather than a zero that reads as "none".
 *  `why` names what an incomplete read left out, when the relay says. */
function PanelHeader({ title, count, complete, note, why, testId }: {
  title: string; count: number | null; complete: boolean | null; note: string; why?: string | null; testId: string;
}) {
  return (
    <div className="border-border flex items-center justify-between gap-2 border-b px-4 py-2.5">
      <div className="flex items-center gap-2">
        <h2 className="text-foreground text-sm font-medium">
          {title}{count !== null && <span className="text-muted-foreground"> · {count}</span>}
        </h2>
        {complete === false && (
          <span className="text-muted-foreground text-[10px]" data-testid={`${testId}-incomplete`}>
            incomplete{why ? ` · ${why}` : ''}
          </span>
        )}
      </div>
      <span className="text-muted-foreground text-[11px]">{note}</span>
    </div>
  );
}

/** #1202: the page's sport chips on US rows, the US convention (the Tracker's,
 *  usMarketClassifier.ts): a market whose sport the catalogue does not give is
 *  visible under every chip and counted as unclassified, never guessed. Runs
 *  only on rows the owner filter already passed, so `hidden` counts only what
 *  the sport hid. */
function bySport<R>(rows: readonly R[], slugOf: (row: R) => string | null, sport: (slug: string) => string | null, filter: SportFilter) {
  if (filter.size === 0) return { rows, hidden: 0, pending: 0 };
  let hidden = 0;
  let pending = 0;
  const shown = rows.filter((row) => {
    const slug = slugOf(row);
    const s = slug === null ? null : sport(slug);
    if (s === null) { pending++; return true; }
    if (!sportMatchesFilter(s as Parameters<typeof sportMatchesFilter>[0], filter)) { hidden++; return false; }
    return true;
  });
  return { rows: shown, hidden, pending };
}

export function UsOrgSection({ orgQueryParam, ownerFilter, sportFilter, marketClient, refreshKey = 0, onRetry, refreshing = false }: {
  orgQueryParam: string;
  ownerFilter: OwnerFilterKey;
  /** The page's sport chips. Empty = no chip: no catalogue read, rows as before. */
  sportFilter: SportFilter;
  /** The catalogue client; tests inject one. Default: one account-free browser client per mount. */
  marketClient?: Pick<UsMarketClient, 'listPublicMarkets'>;
  /** Bumped by the page's Refresh button; the shown rows stay until the new read lands. */
  refreshKey?: number;
  /** The page's Refresh handler, offered as Retry when a read failed. */
  onRetry?: () => void;
  /** The page's own reads are in flight: Retry (the page Refresh) waits for them. */
  refreshing?: boolean;
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const accounts = await checkUsAccounts(orgQueryParam);
      if (accounts !== 'present') {
        if (!cancelled) setLoaded({ key: orgQueryParam, refresh: refreshKey, accounts, positions: null, fills: null });
        return;
      }
      const [positions, fills] = await Promise.all([
        readJson(`${API}/org/positions${orgQueryParam}`),
        readJson(`${API}/org/fills${orgQueryParam}`),
      ]);
      if (!cancelled) setLoaded({ key: orgQueryParam, refresh: refreshKey, accounts, positions: orgPositions(positions), fills: orgFills(fills) });
    })();
    return () => { cancelled = true; };
  }, [orgQueryParam, refreshKey]);

  const current = loaded?.key === orgQueryParam ? loaded : null;

  // #1202: the markets' sports, read only while a chip is on.
  const [lookup] = useState(() => new UsOrgSportLookup(marketClient ?? createUsBrowserMarketClient()));
  useEffect(() => {
    lookup.open();
    return () => lookup.close();
  }, [lookup]);
  const chipOn = sportFilter.size > 0;
  // A Refresh with a chip on asks the catalogue again for the markets it did
  // not hold; the re-read's rows then request them once.
  const seenRefresh = useRef(refreshKey);
  useEffect(() => {
    if (seenRefresh.current === refreshKey) return;
    seenRefresh.current = refreshKey;
    if (chipOn) lookup.retryMisses();
  }, [lookup, refreshKey, chipOn]);
  useEffect(() => {
    const slugs = new Set<string>();
    if (chipOn) {
      for (const p of current?.positions?.rows ?? []) slugs.add(p.row.target.marketSlug);
      for (const f of current?.fills?.rows ?? []) if (f.marketSlug !== null) slugs.add(f.marketSlug);
    }
    // No chip: an empty request, which drops any queued read.
    lookup.request(slugs);
  }, [lookup, current, chipOn]);
  const sportsVersion = useSyncExternalStore(lookup.subscribe, lookup.getVersion);

  const positions = useMemo(() => {
    void sportsVersion;
    const owned = current?.positions?.rows.filter((p) => ownerMatchesFilter(p.owner, ownerFilter)) ?? [];
    return bySport(owned, (p) => p.row.target.marketSlug, (slug) => lookup.get(slug)?.sport ?? null, sportFilter);
  }, [current, ownerFilter, sportFilter, lookup, sportsVersion]);
  const fills = useMemo(() => {
    void sportsVersion;
    const owned = current?.fills?.rows.filter((f) => f.owners.some((o) => ownerMatchesFilter(o, ownerFilter))) ?? [];
    // A disputed fill's market is unknown, so it stays visible and counted as unclassified.
    return bySport(owned, (f) => f.marketSlug, (slug) => lookup.get(slug)?.sport ?? null, sportFilter);
  }, [current, ownerFilter, sportFilter, lookup, sportsVersion]);
  const filtered = ownerFilter !== 'all' || chipOn;
  // A re-read (the page Refresh, or Retry) is in flight until its answer lands.
  const busy = refreshing || current?.refresh !== refreshKey;
  // Four states, never merged: checking, failed, no US (confirmed), US.
  if (!current) return <UsStatusRegion state="loading" testId="org-detail-us-status" />;
  if (current.accounts === 'failed') return <UsStatusRegion state="failed" testId="org-detail-us-status" onRetry={onRetry} busy={busy} />;
  if (current.accounts === 'absent') return null;
  const unlisted = current.fills?.unlistedFormerMembers ?? 0;

  return (
    <>
      <div className="bg-card border-border rounded-lg border" data-testid="org-detail-us-positions">
        <PanelHeader title="Polymarket US Positions" count={current.positions ? positions.rows.length : null} complete={current.positions?.complete ?? null}
          note="held contracts only · not in the K+P totals · no US average cost or P&L yet" testId="org-detail-us-positions" />
        {current.positions === null ? (
          <p className="px-4 py-6 text-center text-sm text-amber-500 light:text-amber-700">Polymarket US positions could not be read<UsRetry onRetry={onRetry} busy={busy} /></p>
        ) : positions.rows.length === 0 ? (
          <p className="text-muted-foreground px-4 py-6 text-center text-sm" data-testid="org-detail-us-positions-empty">
            {filtered ? 'No open Polymarket US positions match your filters' : 'No open Polymarket US positions'}
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-center">Side</th>
                <th className="px-3 py-2 text-right">Held</th>
              </tr>
            </thead>
            {/* Market cells are w-full + max-w-0: they take only the width left over and
                truncate, so neither table outgrows its panel (title carries the full name). */}
            <tbody>
              {positions.rows.map(({ row, owner, former, title }) => {
                const view = presentUsPosition(row);
                return (
                  <tr key={`${row.scope.scopeKey}|${usPositionKey(row)}`} className="border-border/40 border-b" title={row.target.marketSlug}>
                    <td className="text-foreground w-full max-w-0 truncate px-4 py-2 text-xs" title={title ?? row.target.marketSlug}>{title ?? row.target.marketSlug}</td>
                    <td className={`max-w-[160px] truncate xl:max-w-[240px] px-3 py-2 text-xs ${former ? 'text-muted-foreground italic' : 'text-foreground/90'}`}
                      title={former ? FORMER_TITLE : owner.label ?? undefined}>{owner.label}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.target.outcome === 'yes'
                        ? 'bg-green-500/20 text-green-400 light:text-green-700' : 'bg-red-500/20 text-red-400 light:text-red-700'}`}>
                        {row.target.outcome.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                      {row.heldQuantity === null
                        ? dash(`Holding not established: ${view.reasons.join(', ') || 'unknown'}`)
                        : view.held}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {current.positions !== null && positions.hidden + positions.pending > 0 && (
          // Bottom padding only when the note shows: it is the card's last line.
          <div className="pb-2">
            <FilterNote hidden={positions.hidden} pending={positions.pending} noun="position" what="sport" testId="org-detail-us-positions-filter-note" />
          </div>
        )}
      </div>

      <div className="bg-card border-border rounded-lg border" data-testid="org-detail-us-fills">
        <PanelHeader title="Polymarket US Trades" count={current.fills ? fills.rows.length : null} complete={current.fills?.complete ?? null}
          note="latest 20 for the org" testId="org-detail-us-fills"
          why={unlisted > 0 ? `${unlisted} former member${unlisted === 1 ? "'s" : "s'"} trades not listed` : null} />
        {current.fills === null ? (
          <p className="px-4 py-6 text-center text-sm text-amber-500 light:text-amber-700">Polymarket US trades could not be read<UsRetry onRetry={onRetry} busy={busy} /></p>
        ) : fills.rows.length === 0 ? (
          // Filtered, the list is the org's latest 20 narrowed by the filters, not an owner's or a sport's history.
          <p className="text-muted-foreground px-4 py-6 text-center text-sm" data-testid="org-detail-us-fills-empty">
            {filtered ? "None of the org's latest 20 US trades" : 'No Polymarket US trades'}
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Market</th>
                <th className="px-3 py-2 text-left">Owner</th>
                <th className="px-3 py-2 text-center">Side</th>
                <th className="whitespace-nowrap px-3 py-2 text-center">Action</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Price</th>
                <th className="px-4 py-2 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {fills.rows.map((f) => (
                <tr key={`${f.accountId}|${f.executionId}`} className="border-border/40 border-b" title={f.marketSlug ?? 'market disputed'}>
                  <td className="text-foreground w-full max-w-0 truncate px-4 py-2 text-xs" title={f.marketTitle ?? f.marketSlug ?? 'market disputed'}><UsFillMarket fill={f} /></td>
                  <td className="max-w-[160px] truncate xl:max-w-[240px] px-3 py-2"><FillOwner owners={f.owners} /></td>
                  <td className="px-3 py-2 text-center text-xs">{f.outcome === null ? dash('side disputed') : f.outcome.toUpperCase()}</td>
                  <td className="px-3 py-2 text-center text-xs">{f.action === null ? dash('action disputed') : f.action.toUpperCase()}</td>
                  <td className="text-foreground px-3 py-2 text-right font-mono text-xs">{f.quantity ?? dash('quantity disputed')}</td>
                  <td className="text-foreground px-3 py-2 text-right font-mono text-xs"><UsFillPrice fill={f} /></td>
                  <td className="text-muted-foreground px-4 py-2 text-right text-[11px] xl:whitespace-nowrap"><UsFillTime fill={f} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {current.fills !== null && fills.hidden + fills.pending > 0 && (
          // Bottom padding only when the note shows: it is the card's last line.
          <div className="pb-2">
            <FilterNote hidden={fills.hidden} pending={fills.pending} noun="trade" what="sport" testId="org-detail-us-fills-filter-note" />
          </div>
        )}
      </div>
    </>
  );
}
